import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import TabBar from "@/components/TabBar";
import SortableTh, { sortRows } from "@/components/SortableTh";
import { Plus, Pencil, Trash2, ShieldCheck, Lock, AlertTriangle, HelpCircle } from "lucide-react";
import InfoTooltip from "@/components/InfoTooltip";

const ROLE_TOOLTIP = [
    "Roles disponibles:",
    "• Cliente: solo ve los tickets que crea en sus empresas.",
    "• Agente: atiende tickets en empresas asignadas (no es admin).",
    "• Administrador: ve todo el catálogo no-confidencial vía 'TODAS'.",
    "• Super Agente: vigila el trabajo de OTROS agentes (no responde por sí mismo).",
    "• Super Cliente: ve los tickets de OTROS clientes en empresas que administra.",
    "• Super Admin: máximo nivel — incluye perímetro Uso Interno automáticamente.",
].join("\n");

const ROLE_OPTS = [
    { v: "customer", l: "Cliente" },
    { v: "agent", l: "Agente" },
    { v: "admin", l: "Administrador" },
    { v: "superadmin", l: "Super Admin" },
    { v: "super_agent", l: "Super Agente" },
    { v: "super_customer", l: "Super Cliente" },
];

// Build a working assignments map keyed by company_id from a user record (handles legacy company_id)
// Shape: { [cid]: { is_admin: bool, is_client: bool } }
function userToAssignmentsMap(u) {
    const map = {};
    if (Array.isArray(u?.company_assignments)) {
        for (const a of u.company_assignments) {
            if (a && a.company_id) map[a.company_id] = { is_admin: !!a.is_admin, is_client: !!a.is_client };
        }
    } else if (u?.company_id) {
        map[u.company_id] = { is_admin: false, is_client: false };
    }
    return map;
}

function CompanyMatrix({ companies, selected, onToggle, onToggleAdmin, onToggleClient, testIdPrefix, isCustomerRole = false, canBeClientRole = false, lockedCompanyIds = [] }) {
    // selected: { [company_id]: { is_admin, is_client } }
    // lockedCompanyIds: array of company_ids whose toggle is forced ON (cannot be unchecked)
    const lockedSet = new Set(lockedCompanyIds || []);

    const ADMIN_TOOLTIP = [
        "Admin de empresa concede:",
        "• Ve TODOS los tickets de esta empresa (no solo los suyos).",
        "• Acceso a empresas confidenciales (otros admins de TODAS no las ven).",
        "• Si marcas Admin sobre la empresa especial TODAS, equivale a admin global de tickets no-confidenciales.",
        "",
        "No cambia el rol global de la persona.",
    ].join("\n");

    const CLIENT_TOOLTIP = [
        "Modo Cliente (solo para Agentes):",
        "• Activado: el AGENTE se comporta como cliente DENTRO de esta empresa.",
        "• Solo verá sus propios tickets + los que tenga asignados (no la empresa entera).",
        "• Puede crear tickets normalmente como cliente.",
        "",
        "Mutuamente excluyente con 'Administrador'.",
    ].join("\n");

    const ASSIG_TOOLTIP = [
        "Asignación a empresa:",
        "• Asignado = la persona pertenece a esta empresa.",
        "• Clientes: solo ven tickets de empresas asignadas.",
        "• Agentes: solo atienden tickets de empresas asignadas.",
        "• Admins: la marca 'TODAS' los expande a todas las empresas no-confidenciales.",
    ].join("\n");

    const TODAS_TOOLTIP = [
        "Empresa especial TODAS:",
        "• No es una empresa real, es un agregador.",
        "• Marcarla como asignada (sin Admin) no aplica.",
        "• Marcarla con Admin = la persona pasa a ver todos los tickets no-confidenciales del sistema.",
        "• Las empresas confidenciales siguen requiriendo asignación explícita.",
    ].join("\n");

    if (!companies.length) return <div className="text-muted">No hay empresas configuradas.</div>;
    return (
        <div style={{ border: "1px solid var(--border-strong)", borderRadius: 8, overflow: "hidden" }}>
            {isCustomerRole && (
                <div style={{ padding: "8px 12px", background: "#26262B", fontSize: 12, color: "#A3A3A8", borderBottom: "1px solid var(--border-strong)" }}>
                    🔒 Los clientes nunca pueden ser administradores de una empresa (regla de seguridad).
                </div>
            )}            <table className="t" style={{ margin: 0 }}>
                <thead>
                    <tr>
                        <th style={{ width: 80, textAlign: "center" }}>
                            <InfoTooltip text={ASSIG_TOOLTIP} placement="bottom" maxWidth={360} testId={`${testIdPrefix}-assig-header-tooltip`}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                                    Asig.
                                    <HelpCircle size={11} style={{ opacity: 0.75 }} />
                                </span>
                            </InfoTooltip>
                        </th>
                        <th>Empresa</th>
                        <th style={{ width: 140, textAlign: "center" }}>
                            <InfoTooltip text={ADMIN_TOOLTIP} placement="bottom" maxWidth={360} testId={`${testIdPrefix}-admin-header-tooltip`}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    <ShieldCheck size={12} /> Administrador
                                    <HelpCircle size={11} style={{ opacity: 0.75 }} />
                                </span>
                            </InfoTooltip>
                        </th>
                        {canBeClientRole && (
                            <th style={{ width: 110, textAlign: "center" }}>
                                <InfoTooltip text={CLIENT_TOOLTIP} placement="bottom" maxWidth={360} testId={`${testIdPrefix}-client-header-tooltip`}>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                        Cliente
                                        <HelpCircle size={11} style={{ opacity: 0.75 }} />
                                    </span>
                                </InfoTooltip>
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {companies.map((c) => {
                        const isSelected = Object.prototype.hasOwnProperty.call(selected, c.company_id);
                        const row = isSelected ? selected[c.company_id] : { is_admin: false, is_client: false };
                        const isAdmin = !!row.is_admin;
                        const isClient = !!row.is_client;
                        const isLocked = lockedSet.has(c.company_id);
                        return (
                            <tr key={c.company_id}>
                                <td style={{ textAlign: "center" }}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected || isLocked}
                                        disabled={isLocked}
                                        onChange={() => !isLocked && onToggle(c.company_id)}
                                        title={isLocked ? "Asignación obligatoria (POR ASIGNAR)" : undefined}
                                        data-testid={`${testIdPrefix}-company-toggle-${c.company_id}`}
                                    />
                                </td>
                                <td>
                                    <b>{c.name}</b>
                                    {c.is_special === "all" && (
                                        <InfoTooltip text={TODAS_TOOLTIP} placement="bottom" maxWidth={380} testId={`${testIdPrefix}-todas-tooltip-${c.company_id}`}>
                                            <span className="badge" style={{ marginLeft: 8, background: "#1d4ed8", color: "white", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                TODAS <HelpCircle size={10} style={{ opacity: 0.85 }} />
                                            </span>
                                        </InfoTooltip>
                                    )}
                                    {c.is_placeholder && (
                                        <span className="badge" style={{ marginLeft: 8 }}>Placeholder</span>
                                    )}
                                    {isLocked && !c.is_placeholder && (
                                        <span className="badge" style={{ marginLeft: 8, background: "var(--bg-2)", color: "var(--text-muted)" }}>Obligatoria</span>
                                    )}
                                    {c.is_confidential && (
                                        <span className="badge" style={{ marginLeft: 8, background: "#991B1B", color: "white" }}>
                                            <Lock size={10} style={{ verticalAlign: "-1px" }} /> Confidencial
                                        </span>
                                    )}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected && isAdmin && !isCustomerRole}
                                        disabled={!isSelected || isCustomerRole || isClient}
                                        onChange={() => !isCustomerRole && !isClient && onToggleAdmin(c.company_id)}
                                        title={isCustomerRole
                                            ? "Los clientes no pueden ser administradores de empresa."
                                            : isClient
                                                ? "No puede ser Administrador y Cliente al mismo tiempo en la misma empresa."
                                                : !isSelected
                                                    ? "Asigna primero la empresa para poder marcarla como Admin."
                                                    : `Marca para que esta persona sea Administradora de "${c.name}". ${ADMIN_TOOLTIP}`}
                                        data-testid={`${testIdPrefix}-company-admin-${c.company_id}`}
                                    />
                                </td>
                                {canBeClientRole && (
                                    <td style={{ textAlign: "center" }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected && isClient}
                                            disabled={!isSelected || isAdmin}
                                            onChange={() => !isAdmin && onToggleClient && onToggleClient(c.company_id)}
                                            title={isAdmin
                                                ? "No puede ser Cliente y Administrador al mismo tiempo en la misma empresa."
                                                : !isSelected
                                                    ? "Asigna primero la empresa para poder marcarla como Cliente."
                                                    : `Marca para que el agente opere como Cliente dentro de "${c.name}". ${CLIENT_TOOLTIP}`}
                                            data-testid={`${testIdPrefix}-company-client-${c.company_id}`}
                                        />
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function WatchedScope({ form, setForm, allUsers, companies, targetUserRole, testIdPrefix }) {
    // targetUserRole: 'agent' (super_agent watches agents) or 'customer' (super_customer watches customers)
    const watchedUsers = form.watched_user_ids || [];
    const watchedCompanies = form.watched_company_ids || [];
    const [userQuery, setUserQuery] = useState("");
    const [compQuery, setCompQuery] = useState("");

    // Personas elegibles base por rol
    const baseEligibleUsers = (allUsers || []).filter((u) => {
        if (targetUserRole === "agent") return ["agent", "admin", "superadmin", "super_agent"].includes(u.role);
        return u.role === "customer" || u.role === "super_customer";
    });
    // Para super_customer: filtrar personas que tengan al menos UNA de las empresas seleccionadas.
    // Para super_agent: muestra todas las personas elegibles (no se filtra por empresa).
    const userBelongsToWatchedCompany = (u) => {
        if (!watchedCompanies.length) return false;
        const ids = new Set([
            ...((u.company_assignments || []).map((a) => a.company_id)),
            u.company_id,
            ...(u.watched_company_ids || []),
        ].filter(Boolean));
        return watchedCompanies.some((c) => ids.has(c));
    };
    const eligibleUsers = targetUserRole === "customer"
        ? baseEligibleUsers.filter(userBelongsToWatchedCompany)
        : baseEligibleUsers;
    const visibleCompanies = (companies || []).filter((c) => c.is_special !== "all");

    const toggleUser = (uid) => {
        const next = watchedUsers.includes(uid) ? watchedUsers.filter((x) => x !== uid) : [...watchedUsers, uid];
        setForm({ ...form, watched_user_ids: next });
    };
    const toggleCompany = (cid) => {
        const next = watchedCompanies.includes(cid) ? watchedCompanies.filter((x) => x !== cid) : [...watchedCompanies, cid];
        // For super_customer: prune watched_user_ids that no longer belong to any remaining watched company.
        if (targetUserRole === "customer") {
            const remainingSet = new Set(next);
            const stillEligible = watchedUsers.filter((uid) => {
                const u = (allUsers || []).find((x) => x.user_id === uid);
                if (!u) return false;
                const ids = new Set([
                    ...((u.company_assignments || []).map((a) => a.company_id)),
                    u.company_id,
                    ...(u.watched_company_ids || []),
                ].filter(Boolean));
                return [...ids].some((c) => remainingSet.has(c));
            });
            setForm({ ...form, watched_company_ids: next, watched_user_ids: stillEligible });
        } else {
            setForm({ ...form, watched_company_ids: next });
        }
    };

    const filteredUsers = eligibleUsers.filter((u) =>
        !userQuery || u.name.toLowerCase().includes(userQuery.toLowerCase()) || u.email.toLowerCase().includes(userQuery.toLowerCase())
    );
    const filteredCompanies = visibleCompanies.filter((c) =>
        !compQuery || c.name.toLowerCase().includes(compQuery.toLowerCase())
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid={`${testIdPrefix}-watched-scope`}>
            <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <label style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".08em" }}>
                        1. Empresas vigiladas — {watchedCompanies.length} seleccionada(s)
                    </label>
                    <input
                        type="text"
                        className="input"
                        placeholder="Buscar empresa…"
                        value={compQuery}
                        onChange={(e) => setCompQuery(e.target.value)}
                        style={{ width: 180, padding: "4px 8px", fontSize: 12 }}
                        data-testid={`${testIdPrefix}-watched-company-search`}
                    />
                </div>
                <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--border-strong)", borderRadius: 8, padding: 6 }}>
                    {filteredCompanies.length === 0 ? (
                        <div className="text-muted text-sm" style={{ padding: 8 }}>Sin coincidencias</div>
                    ) : filteredCompanies.map((c) => (
                        <label key={c.company_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", cursor: "pointer", borderRadius: 4 }} data-testid={`${testIdPrefix}-watched-company-row-${c.company_id}`}>
                            <input
                                type="checkbox"
                                checked={watchedCompanies.includes(c.company_id)}
                                onChange={() => toggleCompany(c.company_id)}
                                data-testid={`${testIdPrefix}-watched-company-${c.company_id}`}
                            />
                            <span style={{ flex: 1, fontSize: 13 }}>
                                {c.name}
                                {c.is_placeholder && <span className="badge" style={{ marginLeft: 6 }}>Placeholder</span>}
                                {c.is_confidential && <span className="badge" style={{ marginLeft: 6, background: "#991B1B", color: "white" }}><Lock size={10} style={{ verticalAlign: "-1px" }} /> Confidencial</span>}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <label style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".08em" }}>
                        2. Personas vigiladas ({targetUserRole === "agent" ? "agentes" : "clientes"}) — {watchedUsers.length} seleccionada(s)
                    </label>
                    <input
                        type="text"
                        className="input"
                        placeholder="Buscar…"
                        value={userQuery}
                        onChange={(e) => setUserQuery(e.target.value)}
                        style={{ width: 180, padding: "4px 8px", fontSize: 12 }}
                        data-testid={`${testIdPrefix}-watched-user-search`}
                    />
                </div>
                <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--border-strong)", borderRadius: 8, padding: 6 }}>
                    {targetUserRole === "customer" && watchedCompanies.length === 0 ? (
                        <div className="text-muted text-sm" style={{ padding: 8 }}>👆 Primero selecciona empresas arriba para ver clientes elegibles.</div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="text-muted text-sm" style={{ padding: 8 }}>Sin coincidencias</div>
                    ) : filteredUsers.map((u) => (
                        <label key={u.user_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", cursor: "pointer", borderRadius: 4 }} data-testid={`${testIdPrefix}-watched-user-row-${u.user_id}`}>
                            <input
                                type="checkbox"
                                checked={watchedUsers.includes(u.user_id)}
                                onChange={() => toggleUser(u.user_id)}
                                data-testid={`${testIdPrefix}-watched-user-${u.user_id}`}
                            />
                            <span style={{ flex: 1, fontSize: 13 }}>
                                {u.name} <span className="text-muted mono" style={{ fontSize: 11 }}>· {u.email}</span>
                            </span>
                            <span className={`badge role-${u.role}`} style={{ fontSize: 10 }}>{u.role}</span>
                        </label>
                    ))}
                </div>
            </div>

            <p className="text-muted text-sm" style={{ margin: 0, fontSize: 11.5, lineHeight: 1.4 }}>
                {targetUserRole === "agent"
                    ? "Verá tickets asignados a estas personas O que pertenezcan a estas empresas."
                    : "Verá tickets capturados por estas personas O que pertenezcan a estas empresas. Solo puedes asignar clientes que pertenezcan a las empresas seleccionadas arriba."}
            </p>
        </div>
    );
}

export default function Agents() {
    const { user } = useAuth();
    const isAdmin = ["admin", "superadmin"].includes(user?.role);
    const isSuper = user?.role === "superadmin";
    const [rows, setRows] = useState([]);
    const [groups, setGroups] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [newForm, setNewForm] = useState({ name: "", email: "", password: "", role: "agent", group_ids: [], assignments: {}, watched_user_ids: [], watched_company_ids: [], is_internal_use: false });
    const [editForm, setEditForm] = useState({ name: "", role: "", group_ids: [], assignments: {}, watched_user_ids: [], watched_company_ids: [], is_internal_use: false });
    const [createTab, setCreateTab] = useState("general");
    const [editTab, setEditTab] = useState("general");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const [u, g, c] = await Promise.all([api.get("/users"), api.get("/groups"), api.get("/companies")]);
            setRows(u.data); setGroups(g.data); setCompanies(c.data);
        } catch (e) { setError(formatApiError(e)); }
    };
    useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

    const [sort, setSortState] = useState({ field: "name", dir: "asc" });
    const setSort = setSortState;

    const filtered = useMemo(() => {
        const base = rows.filter((r) => {
            if (roleFilter !== "all" && r.role !== roleFilter) return false;
            if (!q) return true;
            const s = q.toLowerCase();
            return r.name.toLowerCase().includes(s) || r.email.toLowerCase().includes(s);
        });
        return sortRows(base, sort, {
            provider: (u) => u.auth_provider || "",
            companies: (u) => {
                const cids = new Set([...(u.company_assignments || []).map((a) => a.company_id), u.company_id].filter(Boolean));
                return Array.from(cids).map((id) => (companies.find((c) => c.company_id === id) || {}).name || "").join(", ");
            },
        });
    }, [rows, q, roleFilter, sort, companies]);

    const placeholderCompanyId = useMemo(
        () => (companies.find((c) => c.is_placeholder) || {}).company_id,
        [companies],
    );

    /** Compute default assignments for a given role.
     *  - superadmin: all non-special companies preselected with is_admin=true
     *  - admin: ensure POR ASIGNAR is always checked (is_admin=false by default)
     *  - other roles: no auto-fill */
    const defaultAssignmentsForRole = (role, current = {}) => {
        if (role === "superadmin") {
            const next = {};
            for (const c of companies) {
                if (c.is_special !== "all") next[c.company_id] = { is_admin: true, is_client: false };
            }
            return next;
        }
        if (role === "admin") {
            const next = { ...current };
            if (placeholderCompanyId) next[placeholderCompanyId] = next[placeholderCompanyId] || { is_admin: false, is_client: false };
            return next;
        }
        // Si cambiamos de agent → otro rol, limpiamos is_client (solo válido para agent/super_agent)
        if (role !== "agent" && role !== "super_agent") {
            const cleaned = {};
            for (const [cid, v] of Object.entries(current)) {
                cleaned[cid] = { is_admin: !!v.is_admin, is_client: false };
            }
            return cleaned;
        }
        return current;
    };

    const handleRoleChange = (form, setter) => (newRole) => {
        const nextAssignments = defaultAssignmentsForRole(newRole, form.assignments);
        // Regla de producto: superadmin SIEMPRE tiene is_internal_use=true.
        const nextInternal = newRole === "superadmin" ? true : form.is_internal_use;
        setter({ ...form, role: newRole, assignments: nextAssignments, is_internal_use: nextInternal });
    };

    const openCreate = () => {
        const initialRole = "agent";
        setNewForm({ name: "", email: "", password: "", role: initialRole, group_ids: [], assignments: defaultAssignmentsForRole(initialRole, {}), watched_user_ids: [], watched_company_ids: [], is_internal_use: false });
        setCreateTab("general");
        setCreateOpen(true);
    };
    const openEdit = (u) => {
        setEditing(u);
        const baseAssignments = userToAssignmentsMap(u);
        const ensured = defaultAssignmentsForRole(u.role, baseAssignments);
        setEditForm({
            name: u.name,
            role: u.role,
            group_ids: u.group_ids || [],
            assignments: ensured,
            watched_user_ids: u.watched_user_ids || [],
            watched_company_ids: u.watched_company_ids || [],
            is_internal_use: !!u.is_internal_use,
        });
        setEditTab("general");
    };

    const assignmentsToPayload = (m) =>
        Object.entries(m).map(([company_id, v]) => ({
            company_id,
            is_admin: !!(v && v.is_admin),
            is_client: !!(v && v.is_client),
        }));

    const saveCreate = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            const isSuperRole = newForm.role === "super_agent" || newForm.role === "super_customer";
            const payload = {
                name: newForm.name,
                email: newForm.email,
                password: newForm.password,
                role: newForm.role,
                group_ids: newForm.group_ids,
                company_assignments: isSuperRole ? [] : assignmentsToPayload(newForm.assignments),
                watched_user_ids: isSuperRole ? newForm.watched_user_ids : [],
                watched_company_ids: isSuperRole ? newForm.watched_company_ids : [],
                is_internal_use: !!newForm.is_internal_use,
            };
            await api.post("/agents", payload);
            setCreateOpen(false); await load();
        } catch (err) { setError(formatApiError(err)); }
        finally { setSaving(false); }
    };
    const saveEdit = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            const isSuperRole = editForm.role === "super_agent" || editForm.role === "super_customer";
            const payload = {
                name: editForm.name,
                role: editForm.role,
                group_ids: editForm.group_ids,
                company_assignments: isSuperRole ? [] : assignmentsToPayload(editForm.assignments),
                watched_user_ids: isSuperRole ? editForm.watched_user_ids : [],
                watched_company_ids: isSuperRole ? editForm.watched_company_ids : [],
                is_internal_use: !!editForm.is_internal_use,
            };
            await api.patch(`/users/${editing.user_id}`, payload);
            setEditing(null); await load();
        } catch (err) { setError(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const remove = async (u) => {
        if (!window.confirm(`¿Eliminar persona "${u.email}"? Esta acción no se puede deshacer.`)) return;
        try { await api.delete(`/users/${u.user_id}`); await load(); }
        catch (err) { setError(formatApiError(err)); }
    };

    const toggleGroup = (form, setter, gid) => {
        const has = form.group_ids.includes(gid);
        setter({ ...form, group_ids: has ? form.group_ids.filter((x) => x !== gid) : [...form.group_ids, gid] });
    };

    const toggleAssignment = (form, setter) => (cid) => {
        const next = { ...form.assignments };
        if (Object.prototype.hasOwnProperty.call(next, cid)) delete next[cid];
        else next[cid] = { is_admin: false, is_client: false };
        setter({ ...form, assignments: next });
    };
    const toggleAssignmentAdmin = (form, setter) => (cid) => {
        if (!Object.prototype.hasOwnProperty.call(form.assignments, cid)) return;
        const cur = form.assignments[cid] || { is_admin: false, is_client: false };
        // Mutuamente excluyente con is_client
        setter({ ...form, assignments: { ...form.assignments, [cid]: { is_admin: !cur.is_admin, is_client: cur.is_admin ? cur.is_client : false } } });
    };
    const toggleAssignmentClient = (form, setter) => (cid) => {
        if (!Object.prototype.hasOwnProperty.call(form.assignments, cid)) return;
        const cur = form.assignments[cid] || { is_admin: false, is_client: false };
        // Mutuamente excluyente con is_admin
        setter({ ...form, assignments: { ...form.assignments, [cid]: { is_client: !cur.is_client, is_admin: cur.is_client ? cur.is_admin : false } } });
    };

    if (!isAdmin) return <div className="card card-padded">Acceso restringido.</div>;

    const roleOptionsForEdit = isSuper ? ROLE_OPTS : ROLE_OPTS.filter((r) => r.v !== "admin" && r.v !== "superadmin");

    const renderUserCompanies = (u) => {
        const map = userToAssignmentsMap(u);
        const cids = Object.keys(map);
        if (!cids.length) return "—";
        return cids.map((cid) => {
            const c = companies.find((x) => x.company_id === cid);
            if (!c) return null;
            return (
                <span key={cid} className="badge" style={{ marginRight: 4, marginBottom: 2, background: c.is_special === "all" ? "#1d4ed8" : "var(--bg-2)", color: c.is_special === "all" ? "white" : undefined }}>
                    {c.name}{map[cid] ? " ★" : ""}
                </span>
            );
        });
    };

    return (
        <div data-testid="agents-page">
            <div className="page-head">
                <div>
                    <h1>Personas</h1>
                    <p>Gestiona personas, administradores y clientes de la plataforma. Asigna a múltiples empresas y marca quién es administrador en cada una. Las empresas marcadas como confidenciales no se incluyen en el alcance de la empresa especial "TODAS".</p>
                </div>
                <button className="btn btn-primary" onClick={openCreate} data-testid="agent-create-btn">
                    <Plus size={16} /> Nueva persona
                </button>
            </div>

            {error && <div className="alert alert-error" data-testid="agents-error">{error}</div>}

            <div className="card">
                <div className="card-header">
                    <h3>Listado ({filtered.length})</h3>
                    <div className="tbar">
                        <input className="input" style={{ width: 260 }} placeholder="Buscar por nombre o email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="agents-search" />
                        <select className="select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="agents-role-filter" style={{ width: 170 }}>
                            <option value="all">Todos los roles</option>
                            <option value="superadmin">Super Admin</option>
                            <option value="admin">Administrador</option>
                            <option value="agent">Agente</option>
                            <option value="super_agent">Super Agente</option>
                            <option value="super_customer">Super Cliente</option>
                            <option value="customer">Cliente</option>
                        </select>
                    </div>
                </div>
                <div className="table-wrap">
                    <table className="t">
                        <thead><tr><SortableTh field="name" sort={sort} onChange={setSort}>Persona</SortableTh><SortableTh field="role" sort={sort} onChange={setSort}>Rol</SortableTh><SortableTh field="provider" sort={sort} onChange={setSort}>Proveedor</SortableTh><SortableTh field="companies" sort={sort} onChange={setSort}>Empresas</SortableTh><th style={{ padding: "10px 12px", textAlign: "left", color: "#A3A3A8", fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: "1px solid #26262B" }}>Grupos</th><th style={{ textAlign: "right", padding: "10px 12px", color: "#A3A3A8", fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: "1px solid #26262B" }}>Acciones</th></tr></thead>
                        <tbody>
                            {filtered.length === 0 && (<tr><td colSpan={6} className="text-muted" style={{ textAlign: "center", padding: 30 }}>Sin personas.</td></tr>)}
                            {filtered.map((u) => {
                                const userGroups = groups.filter((g) => u.group_ids?.includes(g.group_id));
                                return (
                                    <tr key={u.user_id} data-testid={`agent-row-${u.user_id}`}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                <div className="avatar">{u.name?.[0]?.toUpperCase() || "·"}</div>
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{u.name}</div>
                                                    <div className="text-muted mono" style={{ fontSize: 11.5 }}>{u.email}</div>
                                                </div>
                                                {u.is_immutable && <span className="badge" style={{ marginLeft: 6 }}>Inmutable</span>}
                                                {u.is_internal_use && <span className="badge" style={{ marginLeft: 6, background: "#F97316", color: "#fff", borderColor: "#EA580C" }} data-testid={`agent-internal-use-${u.user_id}`}>Uso Interno</span>}
                                            </div>
                                        </td>
                                        <td><span className={`badge role-${u.role}`}>{u.role}</span></td>
                                        <td><span className={`badge ${u.auth_provider === "google" ? "provider-google" : ""}`}>{u.auth_provider}</span></td>
                                        <td className="text-muted" style={{ fontSize: 12, maxWidth: 280 }}>{renderUserCompanies(u)}</td>
                                        <td className="text-muted" style={{ fontSize: 12 }}>{userGroups.length ? userGroups.map((g) => g.name).join(", ") : "—"}</td>
                                        <td>
                                            <div className="actions">
                                                {!u.is_immutable && (
                                                    <>
                                                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)} data-testid={`agent-edit-${u.user_id}`}><Pencil size={13} /> Editar</button>
                                                        {u.user_id !== user.user_id && (
                                                            <button className="btn btn-danger btn-sm" onClick={() => remove(u)} data-testid={`agent-delete-${u.user_id}`}><Trash2 size={13} /></button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                title="Nueva persona"
                testId="agent-create-modal"
                footer={
                    <>
                        <button className="btn btn-ghost" onClick={() => setCreateOpen(false)}>Cancelar</button>
                        <button className="btn btn-primary" onClick={saveCreate} disabled={saving} data-testid="agent-create-save">
                            {saving ? <span className="loader" /> : "Crear persona"}
                        </button>
                    </>
                }
            >
                <form onSubmit={saveCreate} className="row" style={{ gap: 14 }}>
                    <TabBar
                        active={createTab}
                        onChange={setCreateTab}
                        testIdPrefix="agent-create"
                        tabs={[
                            { key: "general", label: "Datos generales" },
                            { key: "perms", label: "Empresas y Permisos" },
                            { key: "notif", label: "Notificaciones" },
                        ]}
                    />

                    {/* TAB: Datos generales */}
                    <div style={{ display: createTab === "general" ? "block" : "none" }} className="row" data-testid="agent-create-tab-general-content">
                        <div className="row row-2">
                            <div className="field">
                                <label>Nombre *</label>
                                <input className="input" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} required data-testid="agent-create-name" />
                            </div>
                            <div className="field">
                                <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    Rol *
                                    <InfoTooltip text={ROLE_TOOLTIP} placement="bottom" maxWidth={400} testId="agent-create-role-tooltip">
                                        <HelpCircle size={12} style={{ opacity: 0.7 }} />
                                    </InfoTooltip>
                                </label>
                                <select className="select" value={newForm.role} onChange={(e) => handleRoleChange(newForm, setNewForm)(e.target.value)} data-testid="agent-create-role">
                                    <option value="agent">Agente</option>
                                    {isSuper && <option value="admin">Administrador</option>}
                                    {isSuper && <option value="superadmin">Super Admin</option>}
                                    <option value="super_agent">Super Agente</option>
                                    <option value="super_customer">Super Cliente</option>
                                </select>
                            </div>
                        </div>
                        <div className="field">
                            <label>Correo *</label>
                            <input type="email" className="input" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} required data-testid="agent-create-email" />
                        </div>
                        <div className="field">
                            <label>Contraseña inicial * (mínimo 8)</label>
                            <input type="text" className="input" value={newForm.password} onChange={(e) => setNewForm({ ...newForm, password: e.target.value })} required minLength={8} data-testid="agent-create-password" />
                        </div>
                        <div className="field">
                            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: newForm.role === "superadmin" ? "not-allowed" : "pointer", userSelect: "none", opacity: newForm.role === "superadmin" ? 0.85 : 1 }}>
                                <input
                                    type="checkbox"
                                    checked={newForm.role === "superadmin" ? true : !!newForm.is_internal_use}
                                    disabled={newForm.role === "superadmin"}
                                    onChange={(e) => setNewForm({ ...newForm, is_internal_use: e.target.checked })}
                                    data-testid="agent-create-internal-use"
                                />
                                <span>
                                    <b>Uso Interno</b>
                                    {newForm.role === "superadmin" && (
                                        <span className="badge" style={{ marginLeft: 8, background: "var(--bg-2)", borderColor: "var(--border-strong)", color: "var(--text-muted)", fontSize: 10 }} data-testid="agent-create-internal-locked">
                                            <Lock size={9} style={{ marginRight: 3, verticalAlign: "middle" }} /> Automático (rol superadmin)
                                        </span>
                                    )}
                                    <div className="text-muted" style={{ fontSize: 12 }}>
                                        {newForm.role === "superadmin"
                                            ? "Los superadmin siempre tienen acceso al perímetro Uso Interno. No se puede desactivar."
                                            : "Habilita a esta persona para acceder a empresas y tipos de solicitud marcados como Uso Interno."}
                                    </div>
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* TAB: Empresas y Permisos */}
                    <div style={{ display: createTab === "perms" ? "block" : "none" }} className="row" data-testid="agent-create-tab-perms-content">
                        <div className="field">
                            <label>{newForm.role === "super_agent" || newForm.role === "super_customer" ? "Alcance vigilado" : "Empresas asignadas"}</label>
                            {(newForm.role === "super_agent" || newForm.role === "super_customer") ? (
                                <WatchedScope
                                    form={newForm}
                                    setForm={setNewForm}
                                    allUsers={rows}
                                    companies={companies}
                                    targetUserRole={newForm.role === "super_agent" ? "agent" : "customer"}
                                    testIdPrefix="agent-create"
                                />
                            ) : (
                                <CompanyMatrix
                                    companies={companies}
                                    selected={newForm.assignments}
                                    onToggle={toggleAssignment(newForm, setNewForm)}
                                    onToggleAdmin={toggleAssignmentAdmin(newForm, setNewForm)}
                                    onToggleClient={toggleAssignmentClient(newForm, setNewForm)}
                                    testIdPrefix="agent-create"
                                    isCustomerRole={newForm.role === "customer" || newForm.role === "super_customer"}
                                    canBeClientRole={newForm.role === "agent" || newForm.role === "super_agent"}
                                    lockedCompanyIds={(newForm.role === "admin" || newForm.role === "superadmin") && placeholderCompanyId ? [placeholderCompanyId] : []}
                                />
                            )}
                        </div>
                        <div className="field">
                            <label>Grupos</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {groups.map((g) => (
                                    <button type="button" key={g.group_id} onClick={() => toggleGroup(newForm, setNewForm, g.group_id)} className={"badge"} style={{ cursor: "pointer", background: newForm.group_ids.includes(g.group_id) ? "rgba(220,38,38,.18)" : "var(--bg-2)", borderColor: newForm.group_ids.includes(g.group_id) ? "var(--accent)" : "var(--border-strong)", color: newForm.group_ids.includes(g.group_id) ? "#fff" : "var(--text-muted)" }} data-testid={`agent-create-group-${g.group_id}`}>
                                        {g.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* TAB: Notificaciones (placeholder por ahora) */}
                    <div style={{ display: createTab === "notif" ? "block" : "none" }} className="row" data-testid="agent-create-tab-notif-content">
                        <div className="card" style={{ padding: 18, background: "var(--bg-2)", border: "1px dashed var(--border)" }}>
                            <p className="text-muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
                                Aquí podrás configurar próximamente las preferencias por canal (correo, push), suscripción a alertas, horario laboral del agente y reglas de fuera-de-oficina. Las opciones del usuario para tema (claro/oscuro) y opt-out de correos siguen estando disponibles desde su <b>Mi perfil</b>.
                            </p>
                        </div>
                    </div>
                </form>
            </Modal>

            <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title={`Editar ${editing?.email || ""}`}
                testId="agent-edit-modal"
                footer={
                    <>
                        <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancelar</button>
                        <button className="btn btn-primary" onClick={saveEdit} disabled={saving} data-testid="agent-edit-save">
                            {saving ? <span className="loader" /> : "Guardar"}
                        </button>
                    </>
                }
            >
                {editing && (
                    <form onSubmit={saveEdit} className="row" style={{ gap: 14 }}>
                        <TabBar
                            active={editTab}
                            onChange={setEditTab}
                            testIdPrefix="agent-edit"
                            tabs={[
                                { key: "general", label: "Datos generales" },
                                { key: "perms", label: "Empresas y Permisos" },
                                { key: "notif", label: "Notificaciones" },
                            ]}
                        />

                        {/* TAB: Datos generales */}
                        <div style={{ display: editTab === "general" ? "block" : "none" }} className="row" data-testid="agent-edit-tab-general-content">
                            <div className="field">
                                <label>Nombre</label>
                                <input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="agent-edit-name" />
                            </div>
                            <div className="field">
                                <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    Rol
                                    <InfoTooltip text={ROLE_TOOLTIP} placement="bottom" maxWidth={400} testId="agent-edit-role-tooltip">
                                        <HelpCircle size={12} style={{ opacity: 0.7 }} />
                                    </InfoTooltip>
                                </label>
                                <select className="select" value={editForm.role} onChange={(e) => handleRoleChange(editForm, setEditForm)(e.target.value)} data-testid="agent-edit-role">
                                    {roleOptionsForEdit.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                                </select>
                            </div>
                            {/* Advertencia: cambio de role que afecta el perímetro Uso Interno */}
                            {(() => {
                                const internalCids = new Set(
                                    companies.filter((c) => c.is_internal_use).map((c) => c.company_id)
                                );
                                const internalAssigned = Object.keys(editForm.assignments || {})
                                    .filter((cid) => internalCids.has(cid))
                                    .map((cid) => companies.find((c) => c.company_id === cid)?.name)
                                    .filter(Boolean);
                                const wasSuper = editing?.role === "superadmin";
                                const isSuperNow = editForm.role === "superadmin";
                                const warnings = [];
                                if (wasSuper && !isSuperNow) {
                                    warnings.push("Estás quitando el rol superadmin. El flag 'Uso Interno' ya no se forzará automáticamente; si lo apagas, perderá acceso al perímetro interno.");
                                }
                                if (!isSuperNow && !editForm.is_internal_use && internalAssigned.length > 0) {
                                    warnings.push(`Esta persona tiene ${internalAssigned.length} empresa(s) de Uso Interno asignadas: ${internalAssigned.slice(0, 3).join(", ")}${internalAssigned.length > 3 ? "…" : ""}. Apagar el flag será rechazado; quita primero esas asignaciones.`);
                                }
                                if (warnings.length === 0) return null;
                                return (
                                    <div data-testid="agent-edit-role-warning" style={{
                                        display: "flex", gap: 10, padding: "10px 12px",
                                        background: "var(--pill-warning-bg)", border: "1px solid var(--pill-warning-bd)",
                                        color: "var(--pill-warning-fg)", borderRadius: 8, fontSize: 12.5,
                                    }}>
                                        <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <div>
                                            {warnings.map((w, i) => <div key={i} style={{ marginBottom: i < warnings.length - 1 ? 6 : 0 }}>{w}</div>)}
                                        </div>
                                    </div>
                                );
                            })()}
                            <div className="field">
                                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: editForm.role === "superadmin" ? "not-allowed" : "pointer", userSelect: "none", opacity: editForm.role === "superadmin" ? 0.85 : 1 }}>
                                    <input
                                        type="checkbox"
                                        checked={editForm.role === "superadmin" ? true : !!editForm.is_internal_use}
                                        disabled={editForm.role === "superadmin"}
                                        onChange={(e) => setEditForm({ ...editForm, is_internal_use: e.target.checked })}
                                        data-testid="agent-edit-internal-use"
                                    />
                                    <span>
                                        <b>Uso Interno</b>
                                        {editForm.role === "superadmin" && (
                                            <span className="badge" style={{ marginLeft: 8, background: "var(--bg-2)", borderColor: "var(--border-strong)", color: "var(--text-muted)", fontSize: 10 }} data-testid="agent-edit-internal-locked">
                                                <Lock size={9} style={{ marginRight: 3, verticalAlign: "middle" }} /> Automático (rol superadmin)
                                            </span>
                                        )}
                                        <div className="text-muted" style={{ fontSize: 12 }}>
                                            {editForm.role === "superadmin"
                                                ? "Los superadmin siempre tienen acceso al perímetro Uso Interno. No se puede desactivar."
                                                : "Habilita a esta persona para acceder a empresas y tipos de solicitud marcados como Uso Interno."}
                                        </div>
                                    </span>
                                </label>
                            </div>
                        </div>

                        {/* TAB: Empresas y Permisos */}
                        <div style={{ display: editTab === "perms" ? "block" : "none" }} className="row" data-testid="agent-edit-tab-perms-content">
                            <div className="field">
                                <label>{editForm.role === "super_agent" || editForm.role === "super_customer" ? "Alcance vigilado" : "Empresas asignadas"}</label>
                                {(editForm.role === "super_agent" || editForm.role === "super_customer") ? (
                                    <WatchedScope
                                        form={editForm}
                                        setForm={setEditForm}
                                        allUsers={rows}
                                        companies={companies}
                                        targetUserRole={editForm.role === "super_agent" ? "agent" : "customer"}
                                        testIdPrefix="agent-edit"
                                    />
                                ) : (
                                    <CompanyMatrix
                                        companies={companies}
                                        selected={editForm.assignments}
                                        onToggle={toggleAssignment(editForm, setEditForm)}
                                        onToggleAdmin={toggleAssignmentAdmin(editForm, setEditForm)}
                                        onToggleClient={toggleAssignmentClient(editForm, setEditForm)}
                                        testIdPrefix="agent-edit"
                                        isCustomerRole={editForm.role === "customer" || editForm.role === "super_customer"}
                                        canBeClientRole={editForm.role === "agent" || editForm.role === "super_agent"}
                                        lockedCompanyIds={(editForm.role === "admin" || editForm.role === "superadmin") && placeholderCompanyId ? [placeholderCompanyId] : []}
                                    />
                                )}
                            </div>
                            <div className="field">
                                <label>Grupos</label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {groups.map((g) => (
                                        <button type="button" key={g.group_id} onClick={() => toggleGroup(editForm, setEditForm, g.group_id)} className={"badge"} style={{ cursor: "pointer", background: editForm.group_ids.includes(g.group_id) ? "rgba(220,38,38,.18)" : "var(--bg-2)", borderColor: editForm.group_ids.includes(g.group_id) ? "var(--accent)" : "var(--border-strong)", color: editForm.group_ids.includes(g.group_id) ? "#fff" : "var(--text-muted)" }}>
                                            {g.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* TAB: Notificaciones (placeholder por ahora) */}
                        <div style={{ display: editTab === "notif" ? "block" : "none" }} className="row" data-testid="agent-edit-tab-notif-content">
                            <div className="card" style={{ padding: 18, background: "var(--bg-2)", border: "1px dashed var(--border)" }}>
                                <p className="text-muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
                                    Aquí podrás configurar próximamente las preferencias por canal (correo, push), suscripción a alertas, horario laboral del agente y reglas de fuera-de-oficina. Las opciones de tema y opt-out de correos siguen estando disponibles desde su <b>Mi perfil</b>.
                                </p>
                            </div>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}
